import moment from 'moment';
import Strings from '@/constants/String';

/**
 * One place that decides what a slot *is* right now, so the tile, the summary
 * strip and the selection bar can never disagree about it.
 *
 * The raw API status (`available` / `hold` / `booked` / `unavailable`) is only
 * half the story — a slot that is nominally available but whose window has
 * already passed is not something a vendor can act on. `getSlotState` folds the
 * clock into the status and hands back the single answer everything else reads.
 */

export type SlotState =
  | 'available'
  | 'booked'
  | 'hold'
  | 'blocked'
  | 'expiring'
  | 'expired';

export interface Slot {
  _id?: string;
  start_time: string;
  end_time: string;
  price?: number;
  /** Mirrors `SlotStatus` in `@/components/SlotCard`, so a selection can be
   *  handed straight to `CreateBookingModal` without a cast. */
  status?: 'available' | 'booked' | 'unavailable' | 'hold';
  booked?: boolean;
  isAvailable?: boolean;
  holdExpiresAt?: string;
  turfId?: string;
  sportId?: string;
  courtId?: string;
  discountPercentage?: number;
  discountedPrice?: number;
  pricing?: {
    vendorBasePrice?: number;
    vendorOriginalPrice?: number;
    vendorDiscountValue?: number;
    vendorDiscountType?: string;
  };
}

export type GroupId = 'midnight' | 'morning' | 'afternoon' | 'evening';

/** The four bands the day is cut into, in the order a vendor reads them. */
export const SLOT_GROUPS: {
  id: GroupId;
  name: string;
  timeRange: string;
  /** Inclusive start hour, exclusive end hour. */
  from: number;
  to: number;
}[] = [
  { id: 'morning', name: 'Morning', timeRange: '6 AM – 12 PM', from: 6, to: 12 },
  { id: 'afternoon', name: 'Afternoon', timeRange: '12 PM – 6 PM', from: 12, to: 18 },
  { id: 'evening', name: 'Evening', timeRange: '6 PM – 12 AM', from: 18, to: 24 },
  { id: 'midnight', name: 'Late night', timeRange: '12 AM – 6 AM', from: 0, to: 6 },
];

export const slotHour = (slot: Slot) => parseInt(String(slot?.start_time || '0').split(':')[0], 10) || 0;

export const groupSlots = (slots: Slot[] = []) =>
  SLOT_GROUPS.map((group) => ({
    ...group,
    slots: slots.filter((s) => {
      const h = slotHour(s);
      return h >= group.from && h < group.to;
    }),
  })).filter((group) => group.slots.length > 0);

/** "18:30" → "6:30 PM". */
export const formatTime = (time: string) => {
  const [hours, minutes] = String(time || '').split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return time;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

/** The end of a slot as a real instant — 11 PM → 12 AM rolls to the next day. */
const slotEndAt = (slot: Slot, onDate: Date) => {
  const [startHour, startMinute] = String(slot.start_time).split(':').map(Number);
  const [endHour, endMinute] = String(slot.end_time).split(':').map(Number);

  const start = new Date(onDate);
  start.setHours(startHour, startMinute, 0, 0);

  const end = new Date(onDate);
  end.setHours(endHour, endMinute, 0, 0);
  if (end <= start) end.setDate(end.getDate() + 1);

  return end;
};

export interface SlotView {
  state: SlotState;
  label: string;
  /** Whether a vendor can select it and act on it. */
  selectable: boolean;
  price: number;
  /** Struck-through price, when a discount is on the slot. */
  wasPrice?: number;
  discountLabel?: string;
  /** Minutes remaining on a hold, for the countdown. */
  holdMinutesLeft?: number;
}

export const getSlotView = (
  slot: Slot,
  selectedDate: Date,
  slotDurationInMinutes = 60,
): SlotView => {
  const price = slot?.pricing?.vendorBasePrice ?? slot?.discountedPrice ?? slot?.price ?? 0;
  const discountValue = slot?.pricing?.vendorDiscountValue ?? 0;

  const wasPrice =
    discountValue > 0
      ? slot?.pricing?.vendorOriginalPrice
      : slot?.discountedPrice > 0
        ? slot?.price
        : undefined;

  const discountLabel =
    discountValue > 0
      ? slot?.pricing?.vendorDiscountType === 'PERCENT'
        ? `${discountValue}% off`
        : `₹${discountValue} off`
      : slot?.discountPercentage > 0
        ? `${slot.discountPercentage}% off`
        : undefined;

  const base = { price, wasPrice, discountLabel };

  const isBooked =
    slot?.status === Strings.slotStatus.booked || slot?.booked || slot?.isAvailable === false;

  // Booked outranks the clock: a past booking is still a booking, and the
  // vendor needs to see it on the board rather than have it read as "expired".
  if (isBooked) {
    return { ...base, state: 'booked', label: 'Booked', selectable: false };
  }

  const end = slotEndAt(slot, selectedDate);
  const now = new Date();

  if (now > end) {
    return { ...base, state: 'expired', label: 'Passed', selectable: false };
  }

  if (slot?.status === Strings.slotStatus.hold) {
    return {
      ...base,
      state: 'hold',
      label: 'On hold',
      selectable: false,
      holdMinutesLeft: Math.max(0, moment(slot?.holdExpiresAt).diff(moment(), 'minutes')),
    };
  }

  // Too little of the window left to sell — the API will reject a booking on
  // it, so the board shouldn't offer one either.
  const minutesLeft = (end.getTime() - now.getTime()) / 60000;
  const closingThreshold = Math.min(10, slotDurationInMinutes * 0.2);
  if (minutesLeft <= closingThreshold) {
    return { ...base, state: 'expiring', label: 'Closing', selectable: false };
  }

  if (slot?.status === Strings.slotStatus.unavailable) {
    return { ...base, state: 'blocked', label: 'Blocked', selectable: true };
  }

  return { ...base, state: 'available', label: 'Open', selectable: true };
};

/** Counts for the day summary strip. */
export const summarise = (
  slots: Slot[],
  selectedDate: Date,
  slotDurationInMinutes?: number,
) =>
  slots.reduce(
    (acc, slot) => {
      const { state, price } = getSlotView(slot, selectedDate, slotDurationInMinutes);
      acc[state] = (acc[state] || 0) + 1;
      if (state === 'booked') acc.earned += price;
      return acc;
    },
    { available: 0, booked: 0, hold: 0, blocked: 0, expiring: 0, expired: 0, earned: 0 } as
      Record<SlotState | 'earned', number>,
  );

export const apiDate = (date: Date) => moment(date).format('DD-MM-YYYY');

/* -------------------------------------------------------------- filtering */

/**
 * Which slots the board shows.
 *
 * `upcoming` is the default and the reason this exists: on today's board most
 * of the day has already passed, and those tiles are pure noise — a vendor
 * cannot open, block or sell a slot that is over. Hiding them by default puts
 * the actionable part of the day on screen instead of three screens below it.
 * `all` is the escape hatch for when someone genuinely wants the history.
 */
export type SlotFilter = 'upcoming' | 'available' | 'booked' | 'all';

export const matchesFilter = (state: SlotState, filter: SlotFilter) => {
  switch (filter) {
    case 'available':
      return state === 'available';
    case 'booked':
      // Held slots are booked-in-progress from the vendor's point of view —
      // someone is at checkout with them, so they belong in this view.
      return state === 'booked' || state === 'hold';
    case 'all':
      return true;
    case 'upcoming':
    default:
      return state !== 'expired';
  }
};

/** Counts per filter, for the tab badges. */
export const filterCounts = (
  slots: Slot[],
  selectedDate: Date,
  slotDurationInMinutes?: number,
) => {
  const states = slots.map((s) => getSlotView(s, selectedDate, slotDurationInMinutes).state);
  return {
    upcoming: states.filter((s) => matchesFilter(s, 'upcoming')).length,
    available: states.filter((s) => matchesFilter(s, 'available')).length,
    booked: states.filter((s) => matchesFilter(s, 'booked')).length,
    all: states.length,
  };
};
